import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MarketingPage } from "@/components/marketing/MarketingPage";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DOMAINS, SKILLS, LEVELS } from "@/lib/framework";
import { DOMAIN_IMG, PAGE_BANNERS } from "@/lib/assets";
import api from "@/lib/api";
import {
  Briefcase, Brain, TrendingUp, ArrowRight, BookOpen, Gauge, Sparkles, ShieldCheck,
  Bot, Eye, Layers, MessageSquare, Scale, ListChecks, Lightbulb, Puzzle, RefreshCw,
  Users, Flag, Sprout, HeartPulse, ChevronRight, CheckCircle2,
} from "lucide-react";

const DOMAIN_META = {
  execution: {
    icon: Briefcase,
    tagline: "Getting the work done — reliably, ethically, and with the right tools.",
    why: "Employers hire people they can count on. This domain turns what you know into dependable, professional action — showing up, following through, and doing it the right way.",
  },
  cognitive: {
    icon: Brain,
    tagline: "Thinking well — solving problems, reasoning with evidence, and reflecting.",
    why: "As AI takes over routine tasks, the human edge is judgment. This domain is about thinking clearly, questioning assumptions, and solving the problems that actually matter.",
  },
  growth: {
    icon: TrendingUp,
    tagline: "Rising with others — leading, collaborating, and staying resilient.",
    why: "Careers are built with teams and through change. This domain is about lifting others, working across differences, and bouncing back stronger when things get hard.",
  },
};

const SKILL_DESC = {
  intercultural_comm: { icon: MessageSquare, d: "Share ideas clearly in writing and speech — and truly listen." },
  prof_ethics: { icon: Scale, d: "Act with integrity, accountability, and professionalism." },
  proj_task_mgmt: { icon: ListChecks, d: "Plan, prioritize, and follow through without being told." },
  critical_solving: { icon: Lightbulb, d: "Analyze, weigh evidence, and reason to sound conclusions." },
  data_literacy: { icon: Puzzle, d: "Break problems down and use information to find solutions." },
  systems_thinking: { icon: RefreshCw, d: "Reflect on how you think and learn — and improve your approach." },
  digital_adapt: { icon: Flag, d: "Influence, guide, and bring out the best in a group." },
  collab_leadership: { icon: Users, d: "Work effectively and inclusively with diverse teams." },
  creative_synthesis: { icon: Sprout, d: "Treat challenge and feedback as fuel for getting better." },
  self_resilience: { icon: HeartPulse, d: "Adapt, persist, and recover when things get tough." },
};

const LEVEL_DESC = {
  emerging: "Beginning to apply the skill, with support.",
  developing: "Applies the skill consistently in familiar situations.",
  proficient: "Applies the skill independently across contexts.",
  advanced: "Adapts and models the skill in new, complex situations.",
};

const SKILL_DETAIL = {
  intercultural_comm: { examples: ["Presenting a project to your class", "Writing a clear, professional email", "Explaining a complex idea so anyone gets it"], grow: ["Give a short presentation or join a club", "Ask a mentor to critique your writing", "Practice active listening in group work"] },
  prof_ethics: { examples: ["Citing your sources honestly", "Owning a mistake and fixing it", "Following through on what you promised"], grow: ["Take a professionalism or ethics workshop", "Volunteer for a role others rely on", "Reflect on tough calls in a journal"] },
  proj_task_mgmt: { examples: ["Meeting deadlines without reminders", "Breaking a big assignment into steps", "Balancing work, school, and life"], grow: ["Use a planner or time-blocking", "Set weekly goals and review them", "Try a short productivity module"] },
  critical_solving: { examples: ["Judging whether a source is credible", "Weighing options before you decide", "Spotting the flaw in an argument"], grow: ["Take a logic or research methods course", "Always ask 'what's the evidence?'", "Debate ideas respectfully with peers"] },
  data_literacy: { examples: ["Debugging code or a lab error", "Using data to back a recommendation", "Finding a workaround under constraints"], grow: ["Practice with real datasets", "Enter a case or hackathon", "Take a problem-solving module"] },
  systems_thinking: { examples: ["Noticing how you study best", "Changing tactics after a low grade", "Setting your own learning goals"], grow: ["Keep a quick reflection journal", "Run a 'what worked?' review after exams", "Use self-assessment checklists"] },
  digital_adapt: { examples: ["Leading a group project", "Mentoring a classmate", "Organizing an event or team"], grow: ["Take a leadership role in a club", "Shadow a leader you admire", "Attend a leadership workshop"] },
  collab_leadership: { examples: ["Pulling your weight on a team", "Resolving a disagreement fairly", "Building on other people's ideas"], grow: ["Join team-based activities", "Practice giving and receiving feedback", "Learn a shared tool like a project board"] },
  creative_synthesis: { examples: ["Trying again after a setback", "Asking for feedback to improve", "Taking on a stretch challenge"], grow: ["Reframe failure as data, not defeat", "Set 'not yet' goals", "Celebrate effort, not just outcomes"] },
  self_resilience: { examples: ["Bouncing back from a bad grade", "Staying steady under a heavy load", "Adapting when plans change"], grow: ["Build a support network you can lean on", "Practice small stress-management habits", "Break big challenges into small wins"] },
};

const WHY_POINTS = [
  { icon: Bot, title: "The workplace is changing", d: "AI and automation are reshaping every field. The tasks that remain — and the people who thrive — depend on durable human skills." },
  { icon: Sparkles, title: "Employers demand them", d: "Communication, critical thinking, collaboration and adaptability top nearly every list of what employers say they need most." },
  { icon: Eye, title: "A GPA can't show them", d: "A grade proves you learned the content. It can't prove you can lead a team, solve an ambiguous problem, or adapt under pressure." },
];

const STEPS = [
  { icon: BookOpen, title: "Learn", d: "You build skills across real coursework, projects, work, and campus life." },
  { icon: Layers, title: "Measure", d: "Course outcomes are weight-mapped to 10 skills and rolled into 3 domains." },
  { icon: Gauge, title: "See your TSB", d: "Your Transferable Skills Benchmark scores each skill from 1.0 to 4.0." },
  { icon: ShieldCheck, title: "Grow & verify", d: "Follow targeted next steps; employers can add a verified 360 signal." },
];

export default function Framework() {
  const [openSkill, setOpenSkill] = useState(null);
  const [skillCareers, setSkillCareers] = useState({});
  useEffect(() => { api.get("/skill-careers").then(({ data }) => setSkillCareers(data)).catch(() => {}); }, []);
  return (
    <MarketingPage
      eyebrow="The Framework"
      banner={PAGE_BANNERS.framework}
      title="10 transferable skills. 3 capacity domains. One clear benchmark."
      lead="GPA+ is built on Lone Star College's transferable-skills guide and public durable-skills research (O*NET, America Succeeds). Here's exactly what we measure, why it matters, and how a Transferable Skills Benchmark comes together."
    >
      {/* WHY IT MATTERS */}
      <section>
        <h2 className="font-heading text-2xl font-bold text-[#003057]">Why transferable skills matter now</h2>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
          {WHY_POINTS.map((p) => (
            <div key={p.title} className="rounded-2xl border border-slate-200 bg-white p-6 card-elevate" data-testid="why-point">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#78be21]/15">
                <p.icon className="h-5 w-5 text-[#5fa114]" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold text-[#003057]">{p.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mt-16 rounded-2xl bg-[#003057] p-8 text-white">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#a8e063]">How it works</p>
        <h2 className="mt-2 font-heading text-2xl font-bold">From everyday learning to proven readiness</h2>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative" data-testid="how-step">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><s.icon className="h-5 w-5 text-[#78be21]" /></span>
                <span className="font-mono-num text-2xl font-bold text-[#78be21]">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="mt-3 font-heading text-lg font-bold">{s.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-300">{s.d}</p>
              {i < STEPS.length - 1 && <ArrowRight className="absolute -right-4 top-2 hidden h-5 w-5 text-white/20 lg:block" />}
            </div>
          ))}
        </div>
      </section>

      {/* THE 3 DOMAINS */}
      <section className="mt-16">
        <h2 className="font-heading text-2xl font-bold text-[#003057]">The 3 capacity domains</h2>
        <p className="mt-2 max-w-3xl text-slate-600">
          The 10 skills are grouped into three domains — each answering a question every employer, advisor, and student cares about.
        </p>
        <div className="mt-8 space-y-8">
          {DOMAINS.map((d) => {
            const meta = DOMAIN_META[d.id];
            return (
              <div key={d.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white card-elevate" data-testid="domain-card" style={{ borderTopWidth: 4, borderTopColor: d.color }}>
                <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-[300px,1fr] lg:p-8">
                  <div>
                    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white" data-testid="domain-diagram">
                      <img src={DOMAIN_IMG[d.id]} alt={`${d.name} illustration`} className="mx-auto aspect-square w-full max-w-[240px] object-contain" />
                    </div>
                    <h3 className="mt-4 font-heading text-xl font-bold text-[#003057]">{d.name}</h3>
                    <p className="mt-1 text-sm font-medium text-slate-500">{meta.tagline}</p>
                    <div className="mt-4 rounded-xl bg-slate-50 p-4">
                      <p className="text-xs font-bold uppercase tracking-widest" style={{ color: d.color }}>Why it matters</p>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{meta.why}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {SKILLS.filter((s) => s.domain === d.id).map((s) => {
                      const sm = SKILL_DESC[s.id] || {};
                      const SkillIcon = sm.icon || Sparkles;
                      return (
                        <button key={s.id} onClick={() => setOpenSkill(s)} className="group/skill rounded-xl border border-slate-100 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid="skill-item">
                          <div className="flex items-center gap-2">
                            <SkillIcon className="h-4 w-4 flex-shrink-0" style={{ color: d.color }} />
                            <span className="font-heading font-bold text-[#003057]">{s.name}</span>
                            <ChevronRight className="ml-auto h-4 w-4 text-slate-300 transition-transform group-hover/skill:translate-x-0.5" />
                          </div>
                          <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{sm.d}</p>
                          <span className="mt-2 inline-block text-[11px] font-bold uppercase tracking-widest" style={{ color: d.color }}>Examples &amp; how to grow →</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 4-LEVEL SCALE */}
      <section className="mt-16 rounded-2xl bg-slate-50 p-8">
        <h2 className="font-heading text-2xl font-bold text-[#003057]">The 4-level developmental scale</h2>
        <p className="mt-2 max-w-3xl text-slate-600">
          Every skill — and your overall benchmark — sits on a simple 1.0–4.0 scale, so growth is always visible and the next level is always clear.
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {LEVELS.map((lv, i) => (
            <div key={lv.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid="level-card">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold text-white" style={{ backgroundColor: lv.hex }}>{i + 1}</span>
                <p className="font-heading text-lg font-bold text-[#003057]">{lv.name}</p>
              </div>
              <p className="mt-2 font-mono-num text-sm font-semibold" style={{ color: lv.hex }}>
                {lv.min.toFixed(1)} – {lv.id === "advanced" ? "4.0" : lv.max.toFixed(2)}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{LEVEL_DESC[lv.id]}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW THE TSB IS CALCULATED */}
      <section className="mt-16">
        <h2 className="font-heading text-2xl font-bold text-[#003057]">How the Transferable Skills Benchmark is built</h2>
        <div className="mt-6 grid grid-cols-1 items-stretch gap-4 md:grid-cols-3">
          {[
            { n: "Skills", c: "#005291", d: "Course learning outcomes are weight-mapped to the 10 transferable skills, each scored 1.0–4.0." },
            { n: "Domains", c: "#10b981", d: "Related skills roll up into the 3 capacity domains, showing strengths and gaps at a glance." },
            { n: "Your TSB", c: "#78be21", d: "Domains combine into one Transferable Skills Benchmark — also shown as a 0–100 score." },
          ].map((step, i) => (
            <div key={step.n} className="relative rounded-2xl border border-slate-200 bg-white p-6 card-elevate">
              <span className="inline-block rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest text-white" style={{ backgroundColor: step.c }}>{step.n}</span>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{step.d}</p>
              {i < 2 && <ArrowRight className="absolute -right-3 top-1/2 hidden h-6 w-6 -translate-y-1/2 text-slate-300 md:block" />}
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[#005291]/6 p-5">
          <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#005291]" />
          <p className="text-sm leading-relaxed text-slate-700">
            <span className="font-bold text-[#003057]">Career-aware by design:</span> different careers value different skills.
            Choose a target field and GPA+ re-weights your same skills to that career's demands — your benchmark updates to show
            how well you fit and exactly what to grow next.
          </p>
        </div>
      </section>

      {/* CTA */}
      <div className="mt-14 flex flex-wrap justify-center gap-3">
        <Link to="/login">
          <Button data-testid="framework-try-demo" size="lg" className="rounded-full bg-[#78be21] px-7 text-base font-bold text-[#00243f] hover:bg-[#6aac1c]">
            See it in the demo <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
        <Link to="/research">
          <Button data-testid="framework-research" size="lg" variant="outline" className="rounded-full border-[#003057]/20 px-7 text-base font-bold text-[#003057] hover:bg-[#003057]/5">
            Research & methodology
          </Button>
        </Link>
      </div>

      {/* SKILL DEEP-DIVE DIALOG */}
      <Dialog open={!!openSkill} onOpenChange={(o) => !o && setOpenSkill(null)}>
        <DialogContent className="max-w-lg" data-testid="skill-dialog">
          {openSkill && (() => {
            const dom = DOMAINS.find((x) => x.id === openSkill.domain);
            const det = SKILL_DETAIL[openSkill.id] || { examples: [], grow: [] };
            return (
              <>
                <DialogHeader>
                  <span className="w-fit rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white" style={{ backgroundColor: dom.color }}>{dom.name}</span>
                  <DialogTitle className="font-heading text-2xl text-[#003057]">{openSkill.name}</DialogTitle>
                  <DialogDescription className="text-slate-600">{(SKILL_DESC[openSkill.id] || {}).d}</DialogDescription>
                </DialogHeader>
                <div className="mt-2 space-y-5">
                  <div>
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#003057]"><Eye className="h-4 w-4" style={{ color: dom.color }} /> What it looks like</p>
                    <ul className="mt-2 space-y-1.5">
                      {det.examples.map((e) => (
                        <li key={e} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: dom.color }} />{e}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#003057]"><Sprout className="h-4 w-4 text-[#78be21]" /> How to grow it</p>
                    <ul className="mt-2 space-y-1.5">
                      {det.grow.map((g) => (
                        <li key={g} className="flex items-start gap-2 text-sm text-slate-700"><ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#78be21]" />{g}</li>
                      ))}
                    </ul>
                  </div>
                  {(skillCareers[openSkill.id] || []).length > 0 && (
                    <div data-testid="skill-careers">
                      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#003057]"><Briefcase className="h-4 w-4" style={{ color: dom.color }} /> Careers that value this most</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(skillCareers[openSkill.id] || []).map((c) => (
                          <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold text-[#003057]" style={{ borderColor: `${dom.color}55` }}>
                            {c.name}
                          </span>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-slate-400">Listed in order of how strongly each field emphasizes this skill.</p>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </MarketingPage>
  );
}
